import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  IconButton,
  InputAdornment,
  CircularProgress,
  Chip,
  Button,
  Dialog
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import ClearIcon from '@mui/icons-material/Clear';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import PDFViewer from './PDFViewer';

// ─── helpers ────────────────────────────────────────────────────────────────

const lastAssistantPapers = (msgs) => {
  for (let i = msgs.length - 1; i >= 0; i--) {
    if (msgs[i].role === 'assistant' && msgs[i].papers && msgs[i].papers.length > 0) {
      return msgs[i].papers;
    }
  }
  return null;
};

// ─── component ───────────────────────────────────────────────────────────────

const AISearchView = ({ apiBaseUrl }) => {
  const [messages, setMessages] = useState([]);
  const [sessionRid, setSessionRid] = useState(null);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [viewingPaper, setViewingPaper] = useState(null);

  const bottomRef = useRef(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading]);

  // ── session ────────────────────────────────────────────────────────────────

  const handleNewChat = () => {
    setMessages([]);
    setSessionRid(null);
    setViewingPaper(null);
    setInput('');
  };

  // ── send ───────────────────────────────────────────────────────────────────

  const handleSend = async () => {
    const text = input.trim();
    if (!text || loading) return;

    setMessages(m => [...m, { id: crypto.randomUUID(), role: 'user', text }]);
    setInput('');
    setLoading(true);

    try {
      const res = await fetch(`${apiBaseUrl}/api/search/llm-search`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ query: text, limit: 100, session_rid: sessionRid }),
      });
      const data = await res.json();

      if (data.session_rid) setSessionRid(data.session_rid);

      setMessages(m => {
        const newPapers = (data.papers && data.papers.length > 0)
          ? data.papers
          : (lastAssistantPapers(m) ?? []);
        return [...m, {
          id: crypto.randomUUID(),
          role: 'assistant',
          text: data.agent_response || 'No response.',
          papers: newPapers,
        }];
      });
    } catch {
      setMessages(m => [...m, {
        id: crypto.randomUUID(),
        role: 'assistant',
        text: 'Sorry, I encountered an error. Please try again.',
        papers: lastAssistantPapers(m) ?? [],
      }]);
    } finally {
      setLoading(false);
    }
  };

  // ── markdown rendering ─────────────────────────────────────────────────────

  const parseCitationLinks = (text) =>
    text.replace(/\[CITE:\d+:[^\]]+\]/g, '');

  const renderLineWithMarkdown = (lineText) => {
    const cleanedText = parseCitationLinks(lineText);
    const boldParts = cleanedText.split(/(\*\*[^*]+?\*\*)/g);
    return boldParts.map((boldPart, i) => {
      if (boldPart.match(/^\*\*[^*]+?\*\*$/)) {
        return <strong key={`bold-${i}`} style={{ fontWeight: 700, color: '#1e293b' }}>{boldPart.slice(2, -2)}</strong>;
      }
      const italicParts = boldPart.split(/(\*[^*]+?\*)/g);
      return italicParts.map((italicPart, j) => {
        if (italicPart.match(/^\*[^*]+?\*$/)) {
          return <em key={`italic-${i}-${j}`} style={{ fontStyle: 'italic', color: '#64748b' }}>{italicPart.slice(1, -1)}</em>;
        }
        return italicPart;
      });
    });
  };

  const renderTable = (tableLines) => {
    if (tableLines.length < 2) return null;
    const headerRow = tableLines[0].split('|').map(c => c.trim()).filter(Boolean);
    const rows = tableLines.slice(2).map(line =>
      line.split('|').map(c => c.trim()).filter(Boolean)
    );
    return (
      <Box sx={{ overflowX: 'auto', mb: 3, borderRadius: '12px', border: '1px solid #e5e7eb' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.95rem' }}>
          <thead>
            <tr>
              {headerRow.map((h, i) => (
                <th key={i} style={{ textAlign: 'left', padding: '14px 16px', fontWeight: 700, color: '#1e293b', borderBottom: '2px solid #cbd5e1', backgroundColor: '#f8fafc' }}>
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => (
              <tr key={i} style={{ backgroundColor: i % 2 === 0 ? '#ffffff' : '#f9fafb' }}>
                {row.map((cell, j) => (
                  <td key={j} style={{ padding: '12px 16px', borderBottom: '1px solid #e5e7eb', color: '#475569', lineHeight: 1.6 }}>
                    {renderLineWithMarkdown(cell)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </Box>
    );
  };

  const renderTextWithMarkdown = (text) => {
    const lines = text.split('\n');
    const elements = [];
    let currentParagraph = [];
    let currentList = [];
    let currentTable = [];
    let inTable = false;

    const flushParagraph = () => {
      if (currentParagraph.length > 0) {
        elements.push(
          <Typography key={`p-${elements.length}`} variant="body1" sx={{ lineHeight: 1.7, mb: 1.5, color: '#475569', fontSize: '1rem' }}>
            {currentParagraph.map((pLine, i) => (
              <React.Fragment key={i}>
                {renderLineWithMarkdown(pLine)}
                {i < currentParagraph.length - 1 && ' '}
              </React.Fragment>
            ))}
          </Typography>
        );
        currentParagraph = [];
      }
    };

    const flushList = () => {
      if (currentList.length > 0) {
        elements.push(
          <Box key={`list-${elements.length}`} component="ul" sx={{ pl: 3, mb: 1.5, mt: 0.5, listStyleType: 'disc', '& li': { mb: 0.5, lineHeight: 1.6, color: '#475569', fontSize: '1rem', '&::marker': { color: '#2563eb' } } }}>
            {currentList.map((item, i) => (
              <li key={i}>
                <Typography variant="body1" component="span" sx={{ color: '#475569' }}>
                  {renderLineWithMarkdown(item)}
                </Typography>
              </li>
            ))}
          </Box>
        );
        currentList = [];
      }
    };

    const flushTable = () => {
      if (currentTable.length > 0) {
        elements.push(<React.Fragment key={`table-${elements.length}`}>{renderTable(currentTable)}</React.Fragment>);
        currentTable = [];
      }
    };

    lines.forEach((line, idx) => {
      const trimmed = line.trim();

      if (trimmed === '---' || trimmed === '***' || trimmed === '___') {
        flushParagraph(); flushList(); flushTable();
        elements.push(<Box key={`sep-${idx}`} sx={{ height: '1px', bgcolor: '#e5e7eb', my: 4, mx: 'auto', width: '80%', borderRadius: '1px' }} />);
        return;
      }

      if (trimmed.startsWith('|') && trimmed.endsWith('|')) {
        flushParagraph(); flushList();
        inTable = true;
        currentTable.push(line);
        return;
      } else if (inTable) {
        flushTable();
        inTable = false;
      }

      if (trimmed.startsWith('## ')) {
        flushParagraph(); flushList();
        elements.push(
          <Typography key={`h2-${idx}`} variant="h5" sx={{ fontWeight: 700, color: '#1e293b', mt: 4, mb: 2, fontSize: '1.5rem', letterSpacing: '-0.02em', borderBottom: '2px solid #e5e7eb', pb: 1 }}>
            {trimmed.replace('## ', '')}
          </Typography>
        );
        return;
      }

      if (trimmed.startsWith('### ')) {
        flushParagraph(); flushList();
        elements.push(
          <Typography key={`h3-${idx}`} variant="h6" sx={{ fontWeight: 700, color: '#2563eb', mt: 3, mb: 1.5, fontSize: '1.15rem', letterSpacing: '-0.02em' }}>
            {trimmed.replace('### ', '')}
          </Typography>
        );
        return;
      }

      if (trimmed.match(/^[-*]\s+/) || trimmed.match(/^\d+\.\s+/)) {
        flushParagraph();
        currentList.push(trimmed.replace(/^[-*]\s+/, '').replace(/^\d+\.\s+/, ''));
        return;
      }

      if (trimmed.match(/^(Title|Authors|Summary|Reference|Citation|Key Researchers and Institutions|Area of Expertise|Region):?\s*$/i)) {
        flushParagraph(); flushList();
        elements.push(
          <Typography key={`label-${idx}`} variant="subtitle1" sx={{ fontWeight: 700, color: '#1e293b', mt: 2, mb: 0.25, fontSize: '1.05rem', textTransform: 'uppercase', letterSpacing: '0.05em', opacity: 0.9 }}>
            {trimmed}
          </Typography>
        );
        return;
      }

      if (trimmed === '') {
        flushParagraph(); flushList();
        return;
      }

      flushList();
      currentParagraph.push(line);
    });

    flushParagraph(); flushList(); flushTable();
    return elements;
  };

  // ── sub-components ─────────────────────────────────────────────────────────

  const PaperCard = ({ paper }) => (
    <Paper
      elevation={0}
      onClick={() => setViewingPaper(paper)}
      sx={{
        p: 2,
        bgcolor: '#f0f9ff',
        border: '1px solid #60a5fa',
        borderLeft: '3px solid #2563eb',
        borderRadius: '8px',
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        boxShadow: '0 1px 3px rgba(37,99,235,0.08)',
        '&:hover': {
          bgcolor: '#dbeafe',
          borderColor: '#3b82f6',
          borderLeftColor: '#1d4ed8',
          transform: 'translateX(4px)',
          boxShadow: '0 2px 8px rgba(37,99,235,0.15)',
        },
      }}
    >
      <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#1e293b', fontSize: '0.95rem', mb: 0.75, lineHeight: 1.3 }}>
        {paper.title}
      </Typography>
      {paper.authors && paper.authors.length > 0 && (
        <Typography variant="body2" sx={{ color: '#64748b', fontSize: '0.8rem', mb: 0.75, lineHeight: 1.3 }}>
          <strong style={{ color: '#475569' }}>Authors:</strong>{' '}{paper.authors.join(', ')}
        </Typography>
      )}
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        {paper.year && (
          <Chip label={`Year: ${paper.year}`} size="small" sx={{ bgcolor: '#ffffff', color: '#2563eb', fontWeight: 600, fontSize: '0.75rem', height: '20px', border: '1px solid #93c5fd' }} />
        )}
        {paper.conference && (
          <Chip label={paper.conference} size="small" sx={{ bgcolor: '#ffffff', color: '#2563eb', fontWeight: 600, fontSize: '0.75rem', height: '20px', border: '1px solid #93c5fd' }} />
        )}
      </Box>
    </Paper>
  );

  const UserBubble = ({ message }) => (
    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
      <Paper
        elevation={0}
        sx={{
          px: 2.5,
          py: 1.5,
          maxWidth: '70%',
          bgcolor: '#2563eb',
          color: 'white',
          borderRadius: '18px 18px 4px 18px',
          boxShadow: '0 2px 8px rgba(37,99,235,0.25)',
        }}
      >
        <Typography variant="body1" sx={{ color: 'white', lineHeight: 1.6, fontSize: '1rem' }}>
          {message.text}
        </Typography>
      </Paper>
    </Box>
  );

  const AssistantBubble = ({ message }) => (
    <Box sx={{ display: 'flex', justifyContent: 'flex-start', mb: 2 }}>
      <Paper
        elevation={0}
        sx={{
          px: 3,
          py: 2.5,
          maxWidth: '85%',
          bgcolor: '#ffffff',
          border: '1px solid #e5e7eb',
          borderRadius: '4px 18px 18px 18px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
        }}
      >
        {message.papers && message.papers.length > 0 && (
          <Box sx={{ mb: 2.5 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#1e293b', mb: 1.5, fontSize: '0.9rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Referenced Papers
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {message.papers.map(paper => (
                <PaperCard key={paper.id} paper={paper} />
              ))}
            </Box>
            <Box sx={{ height: '1px', bgcolor: '#e5e7eb', mt: 2.5, mb: 2 }} />
          </Box>
        )}
        {renderTextWithMarkdown(message.text)}
      </Paper>
    </Box>
  );

  const LoadingBubble = () => (
    <Box sx={{ display: 'flex', justifyContent: 'flex-start', mb: 2 }}>
      <Paper
        elevation={0}
        sx={{
          px: 3,
          py: 2,
          bgcolor: '#ffffff',
          border: '1px solid #e5e7eb',
          borderRadius: '4px 18px 18px 18px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
        }}
      >
        <CircularProgress size={16} sx={{ color: '#2563eb' }} />
        <Typography variant="body2" sx={{ color: '#64748b', fontWeight: 500 }}>
          Analyzing papers...
        </Typography>
      </Paper>
    </Box>
  );

  // ── example questions ──────────────────────────────────────────────────────

  const exampleQuestions = [
    'Papers about GaN power amplifiers from 2015',
    'Recent advances in MOCVD technology',
    'Who are the leading researchers in compound semiconductors?',
    'Summarize packaging trends in wireless devices',
  ];

  // ── render ─────────────────────────────────────────────────────────────────

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 300px)', minHeight: '500px' }}>

      {/* Header */}
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        px: 2,
        py: 1.5,
        borderBottom: '1px solid #e5e7eb',
        bgcolor: '#f8fafc',
        borderRadius: '12px 12px 0 0',
        flexShrink: 0,
      }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1e293b', fontSize: '1rem' }}>
          AI Research Assistant
        </Typography>
        <Button
          startIcon={<RestartAltIcon />}
          onClick={handleNewChat}
          size="small"
          variant="outlined"
          disabled={messages.length === 0 && !sessionRid}
          sx={{
            borderColor: '#e5e7eb',
            color: '#64748b',
            borderRadius: '8px',
            textTransform: 'none',
            fontSize: '0.85rem',
            '&:hover': { borderColor: '#2563eb', color: '#2563eb', bgcolor: '#eff6ff' },
            '&.Mui-disabled': { borderColor: '#f1f5f9', color: '#cbd5e1' },
          }}
        >
          New Chat
        </Button>
      </Box>

      {/* Message list */}
      <Box sx={{
        flex: 1,
        overflowY: 'auto',
        px: 3,
        py: 2,
        bgcolor: '#f8fafc',
        '&::-webkit-scrollbar': { width: '6px' },
        '&::-webkit-scrollbar-track': { background: 'transparent' },
        '&::-webkit-scrollbar-thumb': { background: '#cbd5e1', borderRadius: '3px', '&:hover': { background: '#94a3b8' } },
      }}>

        {/* Empty state */}
        {messages.length === 0 && !loading && (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', gap: 3 }}>
            <Typography variant="h6" sx={{ color: '#94a3b8', fontWeight: 600 }}>
              Ask a question to get started
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, justifyContent: 'center', maxWidth: '600px' }}>
              {exampleQuestions.map((q, i) => (
                <Chip
                  key={i}
                  label={q}
                  size="medium"
                  onClick={() => setInput(q)}
                  sx={{
                    cursor: 'pointer',
                    bgcolor: '#ffffff',
                    color: '#475569',
                    fontWeight: 500,
                    borderRadius: '10px',
                    border: '1px solid #e5e7eb',
                    transition: 'all 0.2s ease',
                    '&:hover': { bgcolor: '#eff6ff', color: '#2563eb', borderColor: '#2563eb', transform: 'translateY(-2px)', boxShadow: '0 4px 8px rgba(37,99,235,0.15)' },
                  }}
                />
              ))}
            </Box>
          </Box>
        )}

        {/* Messages */}
        {messages.map(msg =>
          msg.role === 'user'
            ? <UserBubble key={msg.id} message={msg} />
            : <AssistantBubble key={msg.id} message={msg} />
        )}

        {/* Loading bubble */}
        {loading && <LoadingBubble />}

        {/* Auto-scroll anchor */}
        <div ref={bottomRef} />
      </Box>

      {/* Composer */}
      <Box sx={{
        display: 'flex',
        gap: 1.5,
        px: 2,
        py: 1.5,
        borderTop: '1px solid #e5e7eb',
        bgcolor: '#ffffff',
        borderRadius: '0 0 12px 12px',
        flexShrink: 0,
      }}>
        <TextField
          fullWidth
          multiline
          maxRows={4}
          placeholder="Ask about conference proceedings, authors, topics, or research trends..."
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={e => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              handleSend();
            }
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              bgcolor: '#f8fafc',
              borderRadius: '12px',
              fontSize: '1rem',
              transition: 'all 0.2s ease',
              '& fieldset': { borderColor: '#e5e7eb' },
              '&:hover': { bgcolor: '#ffffff', '& fieldset': { borderColor: '#2563eb' } },
              '&.Mui-focused': { bgcolor: '#ffffff', '& fieldset': { borderColor: '#2563eb', borderWidth: '2px' } },
            },
            '& .MuiOutlinedInput-input': {
              padding: '12px 14px',
              '&::placeholder': { color: '#94a3b8', opacity: 1 },
            },
          }}
          InputProps={{
            endAdornment: input && (
              <InputAdornment position="end">
                <IconButton onClick={() => setInput('')} size="small" sx={{ color: '#94a3b8', '&:hover': { color: '#2563eb' } }}>
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        <IconButton
          onClick={handleSend}
          disabled={!input.trim() || loading}
          sx={{
            bgcolor: '#2563eb',
            color: 'white',
            width: 48,
            height: 48,
            borderRadius: '12px',
            alignSelf: 'flex-end',
            boxShadow: '0 4px 12px rgba(37,99,235,0.3)',
            flexShrink: 0,
            '&:hover': { bgcolor: '#1d4ed8', boxShadow: '0 6px 16px rgba(37,99,235,0.4)', transform: 'translateY(-1px)' },
            '&.Mui-disabled': { bgcolor: '#e5e7eb', color: '#cbd5e1', boxShadow: 'none' },
            transition: 'all 0.2s ease',
          }}
        >
          {loading ? <CircularProgress size={20} color="inherit" /> : <SendIcon fontSize="small" />}
        </IconButton>
      </Box>

      {/* PDF Modal */}
      <Dialog
        open={!!viewingPaper}
        onClose={() => setViewingPaper(null)}
        fullWidth
        maxWidth="lg"
        PaperProps={{ sx: { height: '90vh', borderRadius: '16px', overflow: 'hidden' } }}
      >
        {viewingPaper && (
          <PDFViewer
            paperId={viewingPaper.id}
            paperTitle={viewingPaper.title}
            apiBaseUrl={apiBaseUrl}
            source="ai_search"
            onClose={() => setViewingPaper(null)}
          />
        )}
      </Dialog>

    </Box>
  );
};

export default AISearchView;
